import { Loader2 } from 'lucide-react'
import { type RefObject, useEffect, useState } from 'react'

import { getBufferedEnd, getResumeTargetSec } from '../hooks/useBufferHold'

const POLL_INTERVAL_MS = 500

type BufferStatus = {
  elapsedMs: number
  hasMetadata: boolean
  aheadSec: number
  /** 1 秒あたりに読み込めた動画の秒数（再生速度比）。計測区間が足りない間は null */
  rate: number | null
}

const formatRate = (rate: number): string => (rate >= 10 ? rate.toFixed(0) : rate.toFixed(1))

type Props = {
  videoRef: RefObject<HTMLVideoElement | null>
  playbackRate: number
  getDownloadRate: () => number | null
  /** 読込が追いつかないとき、変換済みの低い画質への切り替えを案内するか */
  canLowerQuality: boolean
}

/** バッファリング中の待ち状況（先読み量・読込速度・残り時間の目安・経過時間）を出す */
export const BufferingOverlay = ({ videoRef, playbackRate, getDownloadRate, canLowerQuality }: Props) => {
  const [status, setStatus] = useState<BufferStatus | null>(null)

  useEffect(() => {
    const startedAt = performance.now()

    const poll = () => {
      const video = videoRef.current
      const elapsedMs = performance.now() - startedAt
      if (!video || video.readyState < HTMLMediaElement.HAVE_METADATA) {
        setStatus({ aheadSec: 0, elapsedMs, hasMetadata: false, rate: null })
        return
      }
      const aheadSec = getBufferedEnd(video) - video.currentTime
      setStatus({ aheadSec, elapsedMs, hasMetadata: true, rate: getDownloadRate() })
    }

    const timer = setInterval(poll, POLL_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [videoRef, getDownloadRate])

  const aheadSec = status?.aheadSec ?? 0
  const rate = status?.rate ?? null
  const targetSec = Math.ceil(getResumeTargetSec(rate, playbackRate))
  const remainingSec = targetSec - aheadSec
  const etaSec = rate && rate > 0 && remainingSec > 0 ? Math.ceil(remainingSec / rate) : null
  // 再生位置のデータが1バイトも届いていない間は、先読み量も速度も 0 のままで目安にならない
  const isReceiving = aheadSec > 0 || (rate ?? 0) > 0
  const isTooSlow = rate !== null && rate < Math.max(1, playbackRate)
  const label = !status?.hasMetadata
    ? '動画の情報を取得中...'
    : isReceiving
      ? 'バッファリング中...'
      : 'データの到着待ち...'

  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center z-10 pointer-events-none">
      <div className="flex flex-col items-center gap-3 bg-black/60 rounded-2xl px-8 py-6">
        <Loader2 className="size-8 text-white animate-spin" />
        <div className="text-gray-200 text-sm">{label}</div>
        {isReceiving && (
          <>
            <div className="w-48 h-2 bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 transition-all duration-300 ease-out"
                style={{ width: `${Math.min(100, (aheadSec / targetSec) * 100)}%` }}
              />
            </div>
            <div className="text-gray-300 text-xs font-mono tabular-nums">
              先読み {Math.min(aheadSec, targetSec).toFixed(1)} / {targetSec}秒
            </div>
          </>
        )}
        <div className="flex flex-col items-center gap-1 text-gray-400 text-xs tabular-nums">
          {isReceiving && rate !== null && (
            <div>
              読込 {formatRate(rate)}倍速{etaSec !== null && ` ・ あと約${etaSec}秒`}
            </div>
          )}
          {status && <div>待機 {Math.floor(status.elapsedMs / 1000)}秒</div>}
          {isTooSlow && canLowerQuality && (
            <div className="text-amber-300">
              回線が再生に追いついていません。変換済みの低い画質にすると待ちが減ります
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
